import { useMemo, useRef, useState } from 'react';
import { API_BASE } from '../../lib/apiBase.js';
import { Badge, Button, Modal, Select } from '../ds/index.js';
import { buildImportRows, normalize, readSpreadsheet, type ImportRole, type ImportRow } from './memberImport.js';
import type { AccessProfile } from './AdminAccessProfiles.js';
import { getTenantSlug } from '../../lib/tenant.js';

type ResultStatus = 'invited' | 'updated' | 'exists' | 'invalid' | 'duplicate' | 'failed';
interface ImportResult { email: string; status: ResultStatus; emailSent?: boolean; member?: unknown; accessError?: string }
interface RowAccess { profileId: string | null; extraIds: string[] }

const SKIP_LABEL: Record<NonNullable<ImportRow['skip']>, string> = {
  invalid: 'Adresse invalide',
  duplicate: 'En double dans le fichier',
  exists: 'Déjà un compte',
};

const ROLE_OPTIONS = [{ value: 'MEMBER', label: 'Membre' }, { value: 'ADMIN', label: 'Admin' }];

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** Valeur du fichier pas encore associée : l'envoi attend un choix. */
const UNSET = '?';

/** Accès actuels d'un compte existant (membre simple : seuls ceux-là sont mis à jour). */
export interface ExistingAccess { role: string; profileId: string | null; extraIds: string[] }

const mappingKey = () => `placio:importAccessMapping:${getTenantSlug()}`;

/** Choix faits au dernier import : valeur du fichier → id de profil ('' = tout le campus). */
function rememberedMapping(): Record<string, string> {
  try {
    const raw = JSON.parse(localStorage.getItem(mappingKey()) ?? '{}') as unknown;
    return raw && typeof raw === 'object' ? raw as Record<string, string> : {};
  } catch { return {}; }
}

function rememberMapping(mapping: Record<string, string>) {
  try {
    const chosen = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v !== UNSET));
    localStorage.setItem(mappingKey(), JSON.stringify({ ...rememberedMapping(), ...chosen }));
  } catch { /* navigation privée */ }
}

/**
 * Profil proposé pour une valeur du fichier : le choix du dernier import s'il
 * existe encore, sinon le profil du même nom (casse et accents ignorés), sinon
 * rien. Jamais « Tout le campus » par défaut : appliqué à un compte existant,
 * il lui retirerait ses accès en plus.
 */
function guessProfile(label: string, profiles: AccessProfile[], remembered: Record<string, string>): string {
  const prev = remembered[label];
  if (prev === '' || (prev && profiles.some((p) => p.id === prev))) return prev;
  return profiles.find((p) => normalize(p.name) === normalize(label))?.id ?? UNSET;
}

/**
 * Inviter plusieurs personnes d'un coup depuis un fichier Excel ou CSV
 * (une colonne e-mail, une colonne statut, des accès facultatifs). Aperçu
 * modifiable avant l'envoi. Avec des accès, les comptes existants sont aussi
 * mis à jour (leur rôle ne change pas).
 */
export function MemberImportModal({ authHeaders, existingEmails, existingAccess, profiles, onClose, onImported }: {
  authHeaders: Record<string, string>;
  existingEmails: Set<string>;
  /** Accès actuels des comptes existants, par e-mail en minuscules. */
  existingAccess: Map<string, ExistingAccess>;
  profiles: AccessProfile[];
  onClose: () => void;
  onImported: (members: unknown[]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [results, setResults] = useState<ImportResult[] | null>(null);

  // Valeur d'accès du fichier → id de profil ('' = aucun accès particulier).
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const labels = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows ?? []) if (r.skip !== 'invalid' && r.skip !== 'duplicate') for (const l of r.accessLabels) counts.set(l, (counts.get(l) ?? 0) + 1);
    return [...counts].sort((a, b) => b[1] - a[1]);
  }, [rows]);
  const usesAccess = labels.length > 0 && profiles.length > 0;

  function accessOf(r: ImportRow): RowAccess & { conflict: boolean } {
    const ids = [...new Set(r.accessLabels.map((l) => mapping[l] ?? '').filter((id) => id && id !== UNSET))];
    const chosen = ids.map((id) => profiles.find((p) => p.id === id)).filter((p): p is AccessProfile => !!p);
    const limits = chosen.filter((p) => p.kind !== 'extra');
    return { profileId: limits[0]?.id ?? null, extraIds: chosen.filter((p) => p.kind === 'extra').map((p) => p.id), conflict: limits.length > 1 };
  }

  const toSend = rows?.filter((r) => !r.skip) ?? [];
  const toUpdate = usesAccess ? (rows?.filter((r) => r.skip === 'exists') ?? []) : [];
  const skipped = rows?.filter((r) => r.skip && !toUpdate.includes(r)) ?? [];
  const conflicts = usesAccess ? [...toSend, ...toUpdate].filter((r) => accessOf(r).conflict).length : 0;
  const profileName = (id: string | null) => profiles.find((p) => p.id === id)?.name;
  const unresolved = usesAccess ? labels.filter(([l]) => (mapping[l] ?? UNSET) === UNSET).length : 0;

  // Ce que l'import retire aux comptes existants : accès en plus perdus, profil « Limite » changé.
  const losses = (() => {
    const lost = new Map<string, number>();
    let limitChanged = 0;
    let accounts = 0;
    for (const r of toUpdate) {
      const cur = existingAccess.get(r.email);
      if (!cur || cur.role !== 'MEMBER') continue;
      const next = accessOf(r);
      const gone = cur.extraIds.filter((id) => !next.extraIds.includes(id));
      for (const id of gone) lost.set(id, (lost.get(id) ?? 0) + 1);
      const limit = cur.profileId !== next.profileId && cur.profileId !== null;
      if (limit) limitChanged++;
      if (gone.length > 0 || limit) accounts++;
    }
    return { lost: [...lost], limitChanged, accounts };
  })();

  async function handleFile(file: File) {
    setReadError(null);
    setRows(null);
    setFileName(file.name);
    try {
      const parsed = buildImportRows(await readSpreadsheet(file), existingEmails);
      if (parsed.length === 0) {
        setReadError('Aucune adresse e-mail trouvée dans ce fichier.');
        return;
      }
      setRows(parsed);
      const found = new Set(parsed.flatMap((r) => r.accessLabels));
      const remembered = rememberedMapping();
      setMapping(Object.fromEntries([...found].map((l) => [l, guessProfile(l, profiles, remembered)])));
    } catch {
      setReadError('Ce fichier n’a pas pu être lu. Enregistre-le en .xlsx ou en .csv, puis réessaie.');
    }
  }

  function setRole(line: number, role: ImportRole) {
    setRows((prev) => prev?.map((r) => (r.line === line ? { ...r, role, note: undefined } as ImportRow : r)) ?? null);
  }

  async function handleSend() {
    if (sending || unresolved > 0 || toSend.length + toUpdate.length === 0) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/members/import`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          members: [...toSend, ...toUpdate].map((r) => {
            if (!usesAccess) return { email: r.email, role: r.role };
            const { profileId, extraIds } = accessOf(r);
            return { email: r.email, role: r.role, access: { profileId, extraIds } };
          }),
        }),
      });
      const body = await res.json().catch(() => ({})) as { results?: ImportResult[]; message?: string };
      if (!res.ok || !body.results) {
        setSendError(body.message ?? 'L’import n’a pas abouti. Réessaie dans un instant.');
        return;
      }
      if (usesAccess) rememberMapping(mapping);
      setResults(body.results);
      onImported(body.results.filter((r) => r.status === 'invited' && r.member).map((r) => r.member));
    } catch {
      setSendError('Erreur réseau. Réessaie dans un instant.');
    } finally {
      setSending(false);
    }
  }

  // ── Résultat ──
  if (results) {
    const invited = results.filter((r) => r.status === 'invited');
    const updated = results.filter((r) => r.status === 'updated');
    const notSent = invited.filter((r) => r.emailSent === false);
    const accessFailed = results.filter((r) => r.accessError);
    // Avec des accès, un compte existant inchangé n'est pas un problème.
    const problems = results.filter((r) => r.status !== 'invited' && r.status !== 'updated' && !(usesAccess && r.status === 'exists'));
    return (
      <Modal title="Import terminé" size="lg" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Fermer</Button>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--color-text-primary)' }}>
          <div className="pl-alert pl-alert-success" role="status">
            {plural(invited.length, 'invitation envoyée', 'invitations envoyées')}
            {usesAccess && ` · ${plural(updated.length, 'compte existant mis à jour', 'comptes existants mis à jour')}`}.
          </div>
          {accessFailed.length > 0 && (
            <div className="pl-alert pl-alert-warning" role="alert">
              Accès non appliqués (profil supprimé entre-temps ?) : {accessFailed.map((r) => r.email).join(', ')}.
            </div>
          )}
          {notSent.length > 0 && (
            <div className="pl-alert pl-alert-warning" role="alert">
              {plural(notSent.length, 'compte créé', 'comptes créés')} sans que l’e-mail parte : utilise « Renvoyer l’invitation » dans la liste.
            </div>
          )}
          {problems.length > 0 && (
            <>
              <p className="pl-page-sub" style={{ margin: 0 }}>Non invités :</p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
                {problems.map((r) => (
                  <li key={r.email}>
                    {r.email || '(vide)'} - {r.status === 'exists' ? 'déjà un compte' : r.status === 'duplicate' ? 'en double' : r.status === 'invalid' ? 'adresse invalide' : 'erreur, à réessayer'}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Importer une liste"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={() => { void handleSend(); }} disabled={sending || unresolved > 0 || toSend.length + toUpdate.length === 0}>
            {sending ? 'Envoi…'
              : toUpdate.length > 0 && toSend.length > 0 ? `Inviter ${plural(toSend.length, 'personne', 'personnes')} et mettre à jour ${toUpdate.length}`
              : toUpdate.length > 0 ? `Mettre à jour ${plural(toUpdate.length, 'compte', 'comptes')}`
              : toSend.length > 0 ? `Inviter ${plural(toSend.length, 'personne', 'personnes')}` : 'Inviter'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: 'var(--color-text-primary)' }}>
        <p className="pl-page-sub" style={{ margin: 0 }}>
          Un fichier Excel (.xlsx) ou CSV avec une colonne <strong>e-mail</strong> et une colonne <strong>statut</strong> (Membre ou Admin).
          Chaque personne reçoit un e-mail pour choisir son mot de passe, valable 24 heures.
          {profiles.length > 0 && <> Une colonne <strong>accès</strong> (facultative) donne aussi les profils d’accès, y compris aux comptes qui existent déjà.</>}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }}
          />
          <Button variant="secondary" onClick={() => fileRef.current?.click()}>{fileName ? 'Choisir un autre fichier' : 'Choisir un fichier'}</Button>
          {fileName && <span className="pl-muted" style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>}
        </div>

        {readError && <div className="pl-alert" role="alert">{readError}</div>}
        {sendError && <div className="pl-alert" role="alert">{sendError}</div>}

        {rows && labels.length > 0 && profiles.length === 0 && (
          <div className="pl-alert pl-alert-warning" role="status">
            Ce fichier contient des accès, mais aucun profil d’accès n’existe encore : crée-les dans l’onglet Accès, puis relance l’import.
          </div>
        )}

        {rows && usesAccess && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="pl-field-label">Accès du fichier</span>
            <p className="pl-help" style={{ margin: 0 }}>
              Choisis le profil qui correspond à chaque valeur. « Tout le campus » : rien de particulier (accès normal).
              Pour un compte qui existe déjà, ces choix remplacent ses accès actuels. Tes choix sont retenus pour le prochain import.
            </p>
            <div className="pl-panel" style={{ overflowX: 'auto' }}>
              <table className="pl-table">
                <thead><tr><th>Dans le fichier</th><th>Personnes</th><th>Profil dans Placio</th></tr></thead>
                <tbody>
                  {labels.map(([label, count]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td className="pl-muted pl-num">{count}</td>
                      <td>
                        <div style={{ width: 260 }}>
                          <Select
                            size="sm"
                            aria-label={`Profil pour « ${label} »`}
                            value={mapping[label] ?? UNSET}
                            options={[
                              ...((mapping[label] ?? UNSET) === UNSET ? [{ value: UNSET, label: 'À choisir' }] : []),
                              { value: '', label: 'Tout le campus' },
                              ...profiles.map((p) => ({ value: p.id, label: `${p.kind === 'extra' ? 'Accès en plus' : 'Limite'} : ${p.name}` })),
                            ]}
                            onChange={(e) => setMapping((prev) => ({ ...prev, [label]: e.target.value }))}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {unresolved > 0 && (
              <div className="pl-alert pl-alert-warning" role="alert">
                {unresolved > 1 ? `${unresolved} valeurs du fichier n’ont pas encore de profil` : 'Une valeur du fichier n’a pas encore de profil'} : choisis-en un pour chacune (ou « Tout le campus ») avant d’envoyer.
              </div>
            )}
            {unresolved === 0 && losses.accounts > 0 && (
              <div className="pl-alert pl-alert-warning" role="alert">
                {plural(losses.accounts, 'compte existant va perdre', 'comptes existants vont perdre')} des accès :
                {' '}{[
                  ...losses.lost.map(([id, n]) => `${profileName(id) ?? 'accès supprimé'} retiré à ${n}`),
                  ...(losses.limitChanged > 0 ? [`profil « Limite » changé pour ${losses.limitChanged}`] : []),
                ].join(', ')}. Vérifie les choix ci-dessus si ce n’est pas voulu.
              </div>
            )}
            {conflicts > 0 && (
              <div className="pl-alert pl-alert-warning" role="alert">
                {plural(conflicts, 'personne a', 'personnes ont')} deux profils « Limite » : un seul est possible, le premier sera gardé.
              </div>
            )}
          </div>
        )}

        {rows && (
          <>
            <p className="pl-page-sub" style={{ margin: 0 }}>
              {plural(toSend.length, 'personne à inviter', 'personnes à inviter')}
              {toUpdate.length > 0 && ` · ${plural(toUpdate.length, 'compte existant à mettre à jour', 'comptes existants à mettre à jour')}`}
              {skipped.length > 0 && ` · ${plural(skipped.length, 'ligne ignorée', 'lignes ignorées')}`}
              . Vérifie les rôles avant d’envoyer.
            </p>
            <div className="pl-panel" style={{ overflowX: 'auto' }}>
              <table className="pl-table">
                <thead>
                  <tr><th>Ligne</th><th>E-mail</th><th>Statut du fichier</th><th>Rôle</th>{usesAccess && <th>Accès</th>}</tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.line} style={r.skip && !toUpdate.includes(r) ? { opacity: 0.55 } : undefined}>
                      <td className="pl-muted pl-num">{r.line}</td>
                      <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email || '(vide)'}</td>
                      <td className="pl-muted">
                        {r.rawStatus || '-'}
                        {r.note && !r.skip && <div style={{ fontSize: 12, color: 'var(--color-warning)' }}>{r.note}</div>}
                      </td>
                      <td>
                        {r.skip ? <Badge tone={r.skip === 'exists' ? 'neutral' : 'danger'}>{toUpdate.includes(r) ? 'Déjà un compte : accès mis à jour' : SKIP_LABEL[r.skip]}</Badge> : (
                          <div style={{ width: 120 }}>
                            <Select size="sm" aria-label={`Rôle de ${r.email}`} value={r.role} options={ROLE_OPTIONS} onChange={(e) => setRole(r.line, e.target.value as ImportRole)} />
                          </div>
                        )}
                      </td>
                      {usesAccess && (
                        <td className="pl-muted" style={{ fontSize: 12 }}>
                          {(r.skip === 'invalid' || r.skip === 'duplicate') ? '-' : (() => {
                            const a = accessOf(r);
                            const names = [profileName(a.profileId) ?? 'Tout le campus', ...a.extraIds.map((id) => `+ ${profileName(id) ?? ''}`)];
                            return names.join(' ');
                          })()}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
